import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { usePageTitle } from '../../hooks/usePageTitle';
import { hubAPI } from '../../services/hubApi';
import { programAPI } from '../../services/programApi';
import { fetchCatalogResources } from '../../services/resourceCatalogApi';
import type { Hub, HubResource, HubSection } from '../../types/hub';
import type { Program, ProgramHighlight, ProgramHub, ProgramSection } from '../../types/program';
import {
  hubOutlineTree,
  orderedByOrder,
  type HubOutlineEntry,
  type HubOutlineNode,
} from '../../utils/hubChapter';
import { placePath } from '../../utils/placePaths';
import { buildResourcePath } from '../../utils/resourceLinks';
import { stripHtml } from '../../utils/htmlEntities';

/** Like hubOutline, but keep a single section visible for the WWG tree. */
function toolkitSectionEntries(sections: HubSection[], resources: HubResource[]): HubOutlineEntry[] {
  const orderedSections = orderedByOrder(sections);
  if (orderedSections.length === 0) return [];

  const entries: HubOutlineEntry[] = orderedSections.map((section) => ({
    id: section.id,
    title: section.title,
    section,
    resources: orderedByOrder(resources.filter((resource) => resource.sectionId === section.id)),
  }));

  const ungrouped = orderedByOrder(resources.filter((resource) => !resource.sectionId));
  if (ungrouped.length > 0) {
    entries.push({
      id: 'more',
      title: 'More resources',
      section: null,
      resources: ungrouped,
    });
  }

  return entries;
}

const CATALOG_CHUNK = 40;

function countWord(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

function MapNode({
  label,
  detail,
  children,
}: {
  label: string;
  detail?: string;
  children?: ReactNode;
}) {
  return (
    <li className="mt-2">
      <p className="text-sm font-semibold text-gray-900">{label}</p>
      {detail && <p className="text-sm text-gray-600">{detail}</p>}
      {children && <ul className="mt-1 ml-4 border-l border-gray-200 pl-4">{children}</ul>}
    </li>
  );
}

function TreeToggle({
  open,
  onToggle,
  label,
  meta,
  kind,
}: {
  open: boolean;
  onToggle: () => void;
  label: string;
  meta?: string;
  kind: string;
}) {
  const Icon = open ? ChevronDownIcon : ChevronRightIcon;
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-start gap-2 rounded-sm py-1.5 text-left focus-ring"
      aria-expanded={open}
    >
      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden />
      <span className="min-w-0">
        <span className="block text-xs font-semibold uppercase tracking-wide text-primary">{kind}</span>
        <span className="block text-sm font-semibold text-gray-900">{label}</span>
        {meta && <span className="block text-xs text-gray-500">{meta}</span>}
      </span>
    </button>
  );
}

function ResourceLeaf({ title, to }: { title: string; to?: string }) {
  return (
    <li className="py-1 pl-6">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Resource</p>
      {to ? (
        <Link to={to} className="text-sm text-primary hover:text-primary-dark focus-ring rounded-sm">
          {title}
        </Link>
      ) : (
        <p className="text-sm text-gray-800">{title}</p>
      )}
    </li>
  );
}

type ResourceLabel = { title: string; slug?: string };

async function loadResourceLabels(wpIds: number[]): Promise<Map<number, ResourceLabel>> {
  const unique = [...new Set(wpIds.filter((id) => Number.isFinite(id) && id > 0))];
  const labels = new Map<number, ResourceLabel>();
  for (let i = 0; i < unique.length; i += CATALOG_CHUNK) {
    const chunk = unique.slice(i, i + CATALOG_CHUNK);
    try {
      const body = await fetchCatalogResources(
        new URLSearchParams({
          wpIds: chunk.join(','),
          perPage: String(chunk.length),
        })
      );
      for (const row of body.data) {
        labels.set(row.wpId, {
          title: stripHtml(row.title) || `Resource ${row.wpId}`,
          slug: row.slug || undefined,
        });
      }
    } catch {
      // Labels stay missing; leaves fall back to id titles.
    }
  }
  for (const id of unique) {
    if (!labels.has(id)) labels.set(id, { title: `Resource ${id}` });
  }
  return labels;
}

function resourceIdsFromHub(hub: Pick<Hub, 'resources'> | ProgramHub): number[] {
  return hub.resources.map((resource: HubResource) => resource.wpPostId);
}

export default function DiscoverabilityReview() {
  usePageTitle('Resource discoverability (WWG)');
  const [programs, setPrograms] = useState<Program[] | null>(null);
  const [toolkits, setToolkits] = useState<Hub[] | null>(null);
  const [labels, setLabels] = useState<Map<number, ResourceLabel>>(new Map());
  const [failed, setFailed] = useState(false);
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    let cancelled = false;
    Promise.all([programAPI.fetchPrograms(), hubAPI.fetchHubs()])
      .then(async ([placeRows, hubRows]) => {
        if (cancelled) return;
        const publishedPlaces = placeRows
          .filter((row) => row.status === 'PUBLISHED')
          .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
        const publishedToolkits = hubRows
          .filter((row) => row.status === 'PUBLISHED')
          .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
        setPrograms(publishedPlaces);
        setToolkits(publishedToolkits);

        const wpIds = [
          ...publishedPlaces.flatMap((place) => [
            ...place.hubs.flatMap((hub) => resourceIdsFromHub(hub)),
            ...place.highlights.map((highlight) => highlight.wpPostId),
          ]),
          ...publishedToolkits.flatMap((hub) => resourceIdsFromHub(hub)),
        ];
        const loadedLabels = await loadResourceLabels(wpIds);
        if (!cancelled) setLabels(loadedLabels);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = (id: string) => {
    setOpenIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const { programPlaces, referencePlaces } = useMemo(() => {
    const rows = programs ?? [];
    return {
      programPlaces: rows.filter((row) => row.kind !== 'REFERENCE'),
      referencePlaces: rows.filter((row) => row.kind === 'REFERENCE'),
    };
  }, [programs]);

  const programTitleById = useMemo(() => {
    const map = new Map<string, string>();
    for (const place of programs ?? []) map.set(place.id, place.title);
    return map;
  }, [programs]);

  const toolkitById = useMemo(() => {
    const map = new Map<string, Hub>();
    for (const hub of toolkits ?? []) map.set(hub.id, hub);
    return map;
  }, [toolkits]);

  const renderResources = (wpIds: number[], emptyNote = 'No resources listed.') => {
    if (wpIds.length === 0) {
      return <p className="pl-6 text-sm text-gray-500">{emptyNote}</p>;
    }
    return (
      <ul className="mt-1 space-y-1">
        {wpIds.map((wpPostId) => {
          const label = labels.get(wpPostId);
          return (
            <ResourceLeaf
              key={wpPostId}
              title={label?.title ?? `Resource ${wpPostId}`}
              to={label?.slug ? buildResourcePath(label.slug) : undefined}
            />
          );
        })}
      </ul>
    );
  };

  const renderHubOutlineNode = (node: HubOutlineNode, keyPrefix: string): ReactNode => {
    const id = `${keyPrefix}:section:${node.id}`;
    const open = openIds.has(id);
    const resourceIds = (node.entry?.resources ?? []).map((resource) => resource.wpPostId);
    const childCount = node.children.length;
    const kind =
      node.entry?.section?.type === 'CONTENT'
        ? 'Content section'
        : childCount > 0
          ? 'Section'
          : 'Section';
    return (
      <li key={id} className="mt-1">
        <TreeToggle
          open={open}
          onToggle={() => toggle(id)}
          kind={kind}
          label={node.label}
          meta={[
            childCount > 0 ? countWord(childCount, 'subsection') : null,
            countWord(resourceIds.length, 'resource'),
          ]
            .filter(Boolean)
            .join(' · ')}
        />
        {open && (
          <div className="ml-2 border-l border-gray-200 pl-3">
            {node.children.length > 0 && (
              <ul>{node.children.map((child) => renderHubOutlineNode(child, id))}</ul>
            )}
            {renderResources(resourceIds, node.children.length > 0 ? 'No resources in this section.' : 'No resources listed.')}
          </div>
        )}
      </li>
    );
  };

  const renderProgramSection = (
    section: ProgramSection,
    sections: ProgramSection[],
    highlights: ProgramHighlight[],
    keyPrefix: string,
    depth: number
  ): ReactNode => {
    const id = `${keyPrefix}:section:${section.id}`;
    const open = openIds.has(id);
    const children = sections
      .filter((item) => item.parentId === section.id)
      .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
    const resourceIds = highlights
      .filter((highlight) => highlight.sectionId === section.id)
      .sort((a, b) => a.order - b.order)
      .map((highlight) => highlight.wpPostId);
    const kind = depth === 0 ? 'Section' : 'Subsection';
    return (
      <li key={id} className="mt-1">
        <TreeToggle
          open={open}
          onToggle={() => toggle(id)}
          kind={kind}
          label={section.title}
          meta={[
            children.length > 0 ? countWord(children.length, 'subsection') : null,
            countWord(resourceIds.length, 'resource'),
          ]
            .filter(Boolean)
            .join(' · ')}
        />
        {open && (
          <div className="ml-2 border-l border-gray-200 pl-3">
            {children.length > 0 && (
              <ul>
                {children.map((child) =>
                  renderProgramSection(child, sections, highlights, id, depth + 1)
                )}
              </ul>
            )}
            {renderResources(
              resourceIds,
              children.length > 0 ? 'No resources in this section.' : 'No resources listed.'
            )}
          </div>
        )}
      </li>
    );
  };

  const renderToolkitBranch = (hub: ProgramHub | Hub, keyPrefix: string) => {
    const full = toolkitById.get(hub.id) ?? hub;
    const id = `${keyPrefix}:toolkit:${full.id}`;
    const open = openIds.has(id);
    const ids = resourceIdsFromHub(full);
    const sectionCount = (full.sections ?? []).length;
    const outline = hubOutlineTree(toolkitSectionEntries(full.sections ?? [], full.resources ?? []));
    const parentTitle =
      toolkitById.get(hub.id)?.program?.title ||
      (full.programId ? programTitleById.get(full.programId) : undefined);
    return (
      <li key={id} className="mt-1">
        <TreeToggle
          open={open}
          onToggle={() => toggle(id)}
          kind="Toolkit"
          label={full.title}
          meta={[
            sectionCount > 0 ? countWord(sectionCount, 'section') : null,
            countWord(ids.length, 'resource'),
            parentTitle ? `In program: ${parentTitle}` : 'Standalone (no parent program)',
          ]
            .filter(Boolean)
            .join(' · ')}
        />
        {open && (
          <div className="ml-2 mt-1">
            {outline.length > 0 ? (
              <div>
                <p className="pl-6 text-xs font-semibold uppercase tracking-wide text-gray-500">Sections</p>
                <ul className="mt-1 border-l border-gray-200 pl-3">
                  {outline.map((node) => renderHubOutlineNode(node, id))}
                </ul>
              </div>
            ) : (
              renderResources(ids)
            )}
          </div>
        )}
      </li>
    );
  };

  const renderPlaceBranch = (place: Program, kindLabel: 'Program' | 'Reference') => {
    const id = `place:${place.id}`;
    const open = openIds.has(id);
    const sections = [...(place.sections ?? [])].sort(
      (a, b) => a.order - b.order || a.title.localeCompare(b.title)
    );
    const highlights = [...(place.highlights ?? [])].sort((a, b) => a.order - b.order);
    const topSections = sections.filter((section) => !section.parentId);
    const toolkitCount = place.hubs.length;
    const underToolkit = new Set(place.hubs.flatMap((hub) => resourceIdsFromHub(hub)));
    const unsectionedHighlights = highlights
      .filter((highlight) => !highlight.sectionId)
      .map((highlight) => highlight.wpPostId)
      .filter((wpPostId) => !underToolkit.has(wpPostId));
    const resourceCount = new Set([
      ...underToolkit,
      ...highlights.map((highlight) => highlight.wpPostId),
    ]).size;
    return (
      <li key={id} className="mt-2 border-b border-gray-100 pb-2">
        <TreeToggle
          open={open}
          onToggle={() => toggle(id)}
          kind={kindLabel}
          label={place.title}
          meta={[
            topSections.length > 0 ? countWord(topSections.length, 'section') : null,
            countWord(toolkitCount, 'toolkit'),
            countWord(resourceCount, 'resource'),
          ]
            .filter(Boolean)
            .join(' · ')}
        />
        {open && (
          <div className="ml-2 mt-1">
            <p className="pl-6 text-xs text-gray-500">
              <Link to={placePath(place)} className="text-primary hover:text-primary-dark focus-ring rounded-sm">
                Open place
              </Link>
            </p>
            {topSections.length > 0 && (
              <div className="mt-2">
                <p className="pl-6 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Sections
                </p>
                <ul className="mt-1 ml-2 border-l border-gray-200 pl-3">
                  {topSections.map((section) =>
                    renderProgramSection(section, sections, highlights, id, 0)
                  )}
                </ul>
              </div>
            )}
            {unsectionedHighlights.length > 0 && (
              <div className="mt-2">
                <p className="pl-6 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Resources (not in a section)
                </p>
                {renderResources(unsectionedHighlights)}
              </div>
            )}
            <div className="mt-2">
              <p className="pl-6 text-xs font-semibold uppercase tracking-wide text-gray-500">Toolkits</p>
              {place.hubs.length > 0 ? (
                <ul className="mt-1 ml-2 border-l border-gray-200 pl-3">
                  {[...place.hubs]
                    .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title))
                    .map((hub) => renderToolkitBranch(hub, id))}
                </ul>
              ) : (
                <p className="pl-6 text-sm text-gray-500">No toolkits nested here.</p>
              )}
            </div>
          </div>
        )}
      </li>
    );
  };

  const loading = !failed && (programs === null || toolkits === null);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <p className="text-xs font-semibold uppercase tracking-wide text-primary">Temporary · WWG review</p>
      <h1 className="mt-2 text-3xl font-bold text-gray-900">Resource discoverability</h1>
      <p className="mt-3 text-gray-600 max-w-3xl">
        How people reach a <strong className="font-semibold text-gray-800">Resource</strong> today, then the live
        place tree using <strong className="font-semibold text-gray-800">Program</strong>,{' '}
        <strong className="font-semibold text-gray-800">Reference</strong>, and{' '}
        <strong className="font-semibold text-gray-800">Toolkit</strong> labels.
      </p>

      <section className="mt-10">
        <h2 className="text-2xl font-bold text-gray-900 border-l-4 border-primary pl-4">Discoverability map</h2>
        <p className="mt-2 text-sm text-gray-600">
          Main nav door is Resources. Library is secondary (not in header/footer). Toolkits are first-class, not only
          nested under a Program. Programs, References, and Toolkits can organize Resources under Sections (and
          Subsections).
        </p>
        <ul className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
          <MapNode
            label="Resources — /resources"
            detail="Primary entry in header and footer. Lists Programs, References, and Toolkits."
          >
            <MapNode label="Program" detail="Clinical topic place; may contain Sections, Toolkits, and Resources.">
              <MapNode label="Section" detail="Heading on the Program; may nest Subsections.">
                <MapNode label="Subsection">
                  <MapNode label="Resource" />
                </MapNode>
                <MapNode label="Resource" />
              </MapNode>
              <MapNode label="Toolkit" detail="Also nested under a Program when assigned.">
                <MapNode label="Section" detail="Toolkit section or slash-nested subsection.">
                  <MapNode label="Resource" />
                </MapNode>
              </MapNode>
            </MapNode>
            <MapNode label="Reference" detail="Lookup material place; same Section pattern as Program.">
              <MapNode label="Section">
                <MapNode label="Subsection">
                  <MapNode label="Resource" />
                </MapNode>
                <MapNode label="Resource" />
              </MapNode>
            </MapNode>
            <MapNode
              label="Toolkit (direct)"
              detail="Listed on /resources and openable at /toolkits/:slug without going through a Program."
            >
              <MapNode label="Section">
                <MapNode label="Resource" />
              </MapNode>
            </MapNode>
          </MapNode>
          <MapNode label="Search" detail="Finds Programs, Toolkits, and Resources.">
            <MapNode label="Program / Toolkit / Resource" />
          </MapNode>
          <MapNode label="Signed-in home" detail="Personal rails; not the public front door.">
            <MapNode label="New for you" detail="Matched Resources; action links to Library.">
              <MapNode label="Resource" />
              <MapNode
                label="Library — /library (secondary)"
                detail="Exists and is linked from New for you; not a main-nav entry."
              >
                <MapNode label="Resource" />
              </MapNode>
            </MapNode>
            <MapNode label="Saved" detail="Saved Resources, Toolkits, and Programs.">
              <MapNode label="Resource / Toolkit / Program" />
            </MapNode>
            <MapNode label="Updates" detail="Changes to Resources you saved.">
              <MapNode label="Resource" />
            </MapNode>
            <MapNode label="Recently viewed" detail="Resources you opened.">
              <MapNode label="Resource" />
            </MapNode>
            <MapNode label="From ORBCoN" detail="Featured Toolkits; action goes to Programs (/resources).">
              <MapNode label="Toolkit">
                <MapNode label="Section">
                  <MapNode label="Resource" />
                </MapNode>
              </MapNode>
            </MapNode>
          </MapNode>
        </ul>
      </section>

      <section className="mt-12">
        <h2 className="text-2xl font-bold text-gray-900 border-l-4 border-primary pl-4">Live place tree</h2>
        <p className="mt-2 text-sm text-gray-600">
          Published data from this environment. Expand a Program, Reference, or Toolkit to see Sections,
          Subsections, and Resources.
        </p>

        {failed && <p className="mt-4 text-sm text-gray-500">Places could not be loaded.</p>}
        {loading && <p className="mt-4 text-sm text-gray-500">Loading places…</p>}

        {!failed && !loading && programs && toolkits && (
          <div className="mt-6 space-y-10">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Programs</h3>
              {programPlaces.length === 0 ? (
                <p className="mt-2 text-sm text-gray-500">No published Programs.</p>
              ) : (
                <ul className="mt-2">{programPlaces.map((place) => renderPlaceBranch(place, 'Program'))}</ul>
              )}
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900">References</h3>
              {referencePlaces.length === 0 ? (
                <p className="mt-2 text-sm text-gray-500">No published References.</p>
              ) : (
                <ul className="mt-2">{referencePlaces.map((place) => renderPlaceBranch(place, 'Reference'))}</ul>
              )}
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900">Toolkits (first-class)</h3>
              <p className="mt-1 text-sm text-gray-600">
                Every published Toolkit, including those nested under a Program and standalone ones.
              </p>
              {toolkits.length === 0 ? (
                <p className="mt-2 text-sm text-gray-500">No published Toolkits.</p>
              ) : (
                <ul className="mt-2 border border-gray-200 rounded-lg bg-white p-3">
                  {toolkits.map((hub) => renderToolkitBranch(hub, 'all'))}
                </ul>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
